<nav
  class="relative z-50 bg-[#26332b] shadow-md"
  role="navigation"
  aria-label="Main navigation"
>
  <div class="container mx-auto px-4">
    <div class="flex items-center justify-between h-16">

      <!-- Left: brand + desktop nav -->
      <div class="flex items-center gap-6">
        <a routerLink="/regulate"
           class="text-xl font-bold text-[#e5ffb2] whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e5ffb2] rounded">
          Quantios Regulatory Intelligence
        </a>

        <!-- Desktop nav: single Regulate link (hidden for operators) -->
        @if (!auth.isOperator()) {
          <ul class="hidden lg:flex items-center gap-1">
            <li>
              <a
                routerLink="/regulate"
                routerLinkActive
                [ariaCurrentWhenActive]="'page'"
                [class]="horizonLinkClass()"
              >
                <lucide-angular name="globe" [size]="16" aria-hidden="true" />
                Regulate
              </a>
            </li>
          </ul>
        }
      </div>

      <!-- Right: user button (desktop) -->
      <div class="hidden lg:flex items-center">
        @if (auth.user(); as user) {
          <div class="relative">
            <button
              type="button"
              class="flex items-center justify-center w-9 h-9 rounded-full bg-[#73716d] text-[#faf6ed] hover:bg-[#4d806f] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e5ffb2]"
              [attr.aria-expanded]="userMenuOpen()"
              aria-haspopup="true"
              aria-controls="dropdown-user"
              (click)="userMenuOpen.update(v => !v)"
            >
              <lucide-angular name="user" [size]="18" aria-hidden="true" />
              <span class="sr-only">{{ user.displayName ?? user.email }}</span>
            </button>

            @if (userMenuOpen()) {
              <div
                class="fixed inset-0 z-40"
                aria-hidden="true"
                (click)="closeUserMenu()"
                (keydown.escape)="closeUserMenu()"
              ></div>
              <div
                id="dropdown-user"
                role="menu"
                class="absolute right-0 top-full mt-2 w-56 bg-white rounded-lg shadow-xl ring-1 ring-black/5 py-1 z-50"
              >
                <div
                  role="menuitem"
                  aria-disabled="true"
                  class="px-4 py-2 text-sm text-gray-400 cursor-default select-none border-b border-gray-100"
                >
                  {{ user.displayName ?? user.email }}
                </div>

                @if (!auth.isOperator()) {
                  <a
                    routerLink="/personalisation"
                    role="menuitem"
                    (click)="closeUserMenu()"
                    class="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#4d806f]"
                  >
                    <lucide-angular name="user" [size]="16" aria-hidden="true" class="text-gray-400 shrink-0" />
                    Personalisation
                  </a>
                }

                @if (auth.isAdmin()) {
                  <a
                    routerLink="/settings"
                    role="menuitem"
                    (click)="closeUserMenu()"
                    class="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#4d806f]"
                  >
                    <lucide-angular name="settings" [size]="16" aria-hidden="true" class="text-gray-400 shrink-0" />
                    Settings
                  </a>
                }

                @if (auth.isOperator()) {
                  <a
                    routerLink="/internal/admin"
                    role="menuitem"
                    (click)="closeUserMenu()"
                    class="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#4d806f]"
                  >
                    <lucide-angular name="shield" [size]="16" aria-hidden="true" class="text-gray-400 shrink-0" />
                    Admin
                  </a>
                }

                <div class="border-t border-gray-100 mt-1 pt-1">
                  <button
                    type="button"
                    role="menuitem"
                    (click)="onLogout()"
                    class="flex w-full items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-red-400"
                  >
                    <lucide-angular name="log-out" [size]="16" aria-hidden="true" class="shrink-0" />
                    Logout
                  </button>
                </div>
              </div>
            }
          </div>
        }
      </div>

      <!-- Hamburger (mobile) -->
      <button
        type="button"
        class="lg:hidden p-2 rounded-md text-[#faf6ed] hover:bg-[#4d806f] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e5ffb2]"
        (click)="toggleMobileMenu()"
        [attr.aria-expanded]="mobileMenuOpen()"
        aria-controls="mobile-menu"
      >
        <span class="sr-only">Toggle navigation menu</span>
        @if (mobileMenuOpen()) {
          <lucide-angular name="x" [size]="22" aria-hidden="true" />
        } @else {
          <lucide-angular name="menu" [size]="22" aria-hidden="true" />
        }
      </button>
    </div>

    <!-- Mobile menu -->
    @if (mobileMenuOpen()) {
      <div id="mobile-menu" class="lg:hidden pb-4">
        <ul class="flex flex-col gap-1">
          @if (!auth.isOperator()) {
            <li>
              <a
                routerLink="/regulate"
                routerLinkActive="bg-[#4d806f] text-white"
                [ariaCurrentWhenActive]="'page'"
                (click)="mobileMenuOpen.set(false)"
                class="flex items-center gap-2 px-3 py-2 rounded-md bg-[#73716d] text-[#faf6ed] text-sm hover:bg-[#4d806f] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e5ffb2]"
              >
                <lucide-angular name="globe" [size]="16" aria-hidden="true" />
                Regulate
              </a>
            </li>
          }
        </ul>

        @if (auth.user(); as user) {
          <div class="mt-3 pt-3 border-t border-[#4d806f] space-y-1">
            <div class="px-3 py-1 text-[#e5ffb2]/60 text-xs font-semibold uppercase tracking-wide">
              {{ user.displayName ?? user.email }}
            </div>
            @if (!auth.isOperator()) {
              <a
                routerLink="/personalisation"
                (click)="mobileMenuOpen.set(false)"
                class="flex items-center gap-2 px-3 py-2 rounded-md text-[#faf6ed] text-sm hover:bg-[#4d806f] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e5ffb2]"
              >
                <lucide-angular name="user" [size]="16" aria-hidden="true" />
                Personalisation
              </a>
            }
            @if (auth.isAdmin()) {
              <a
                routerLink="/settings"
                (click)="mobileMenuOpen.set(false)"
                class="flex items-center gap-2 px-3 py-2 rounded-md text-[#faf6ed] text-sm hover:bg-[#4d806f] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e5ffb2]"
              >
                <lucide-angular name="settings" [size]="16" aria-hidden="true" />
                Settings
              </a>
            }
            @if (auth.isOperator()) {
              <a
                routerLink="/internal/admin"
                (click)="mobileMenuOpen.set(false)"
                class="flex items-center gap-2 px-3 py-2 rounded-md text-[#faf6ed] text-sm hover:bg-[#4d806f] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e5ffb2]"
              >
                <lucide-angular name="shield" [size]="16" aria-hidden="true" />
                Admin
              </a>
            }
            <button
              type="button"
              (click)="onLogout()"
              class="flex w-full items-center gap-2 px-3 py-2 rounded-md text-red-300 text-sm hover:bg-red-900/30 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400"
              aria-label="Log out"
            >
              <lucide-angular name="log-out" [size]="16" aria-hidden="true" />
              Logout
            </button>
          </div>
        }
      </div>
    }
  </div>
</nav>
